import { Sentiment } from 'sidecar-utilities'

/**
 * Statuses that mean Member Care is done with the ticket and the member can no
 * longer reply to it.
 *
 * The member-communication API maps Jira statuses through `StatusMapping`
 * before returning them, which is why `RESOLVED` is uppercase. `Done` and
 * `Closed` have no entry in that map, so they pass through with Jira's own
 * casing — hence the case-insensitive comparison below.
 */
const CLOSED_TICKET_STATUSES = ['resolved', 'closed', 'done']

/**
 * Whether a ticket is still open, i.e. whether the member can reply to it.
 *
 * An unknown or missing status counts as open: we can't prove Member Care is
 * done with it, and wrongly blocking a reply keeps the member's issue from
 * getting resolved.
 *
 * @param {string} status - Ticket status as returned by the API
 * @returns {boolean} True when the ticket accepts replies
 */
export const isTicketOpen = (status?: string): boolean =>
  !CLOSED_TICKET_STATUSES.includes((status ?? '').trim().toLowerCase())

/**
 * MCT Ticket Status utility to return the status and sentiment color value for
 * the Pill component
 *
 * @param {string} status - Ticket Status
 * @returns {object} Status and sentiment color
 */
export const getTicketStatus = (status: string) => {
  // if there is no status, return empty status and sentiment
  if (!status) {
    return { status: '', sentiment: '' }
  }

  // if status is with provider engagement or with account management,
  // replace the word engagement as engmt and replace the work management as mgmt
  if (status.toLowerCase().includes('engagement')) {
    status = status.toLowerCase().replace(/engagement/gi, 'Engmt')
  }
  if (status.toLowerCase().includes('management')) {
    status = status.toLowerCase().replace(/management/gi, 'Mgmt')
  }
  if (status.toLowerCase().includes('for')) {
    status = status.toLowerCase().replace(/ for /gi, ' on ')
  }

  const statusList = [
    { status: 'New', sentiment: Sentiment.ACCENT },
    { status: 'Enrollment', sentiment: Sentiment.NOTIFY },
    { status: 'With Member Care', sentiment: Sentiment.NOTIFY },
    { status: 'With Claims', sentiment: Sentiment.NOTIFY },
    { status: 'With Clinical', sentiment: Sentiment.NOTIFY },
    { status: 'With Provider Engmt', sentiment: Sentiment.NOTIFY },
    { status: 'With Account Mgmt', sentiment: Sentiment.NOTIFY },
    { status: 'Waiting on Provider', sentiment: Sentiment.DEFAULT },
    { status: 'Waiting on Member', sentiment: Sentiment.DEFAULT },
    { status: 'Needs more info', sentiment: Sentiment.RISK },
    { status: 'Waiting on provider', sentiment: Sentiment.NOTIFY },
    { status: 'In progress', sentiment: Sentiment.NOTIFY },
    { status: 'With Dedicated Service', sentiment: Sentiment.NOTIFY },
    { status: 'Pending Resolution', sentiment: Sentiment.ENGAGE },
    { status: 'Corrected', sentiment: Sentiment.ENGAGE },
    { status: 'Confirmed', sentiment: Sentiment.ENGAGE },
    { status: 'Changed', sentiment: Sentiment.ENGAGE },
    { status: 'Completed', sentiment: Sentiment.ENGAGE },
    { status: 'Resolved', sentiment: Sentiment.ENGAGE },
  ]

  const matchedStatus = statusList.find(
    item => item.status.toLowerCase() === status.toLowerCase(),
  )
  // if no matched status, but status is "Member Responded" or "CSA Approval" return the 'In progress' status
  if (
    !matchedStatus &&
    (status.toLowerCase() === 'member responded' ||
      status.toLowerCase() === 'csa approval')
  ) {
    return { status: 'In progress', sentiment: Sentiment.NOTIFY }
  }
  if (matchedStatus) {
    return { status: matchedStatus.status, sentiment: matchedStatus.sentiment }
  } else {
    return { status: '', sentiment: '' }
  }
}
